import React from 'react';
import { Button, Typography } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { isString } from 'lib/typeguards';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Column, Row } from 'ui/Flex';
import { MuiModal } from 'ui/MuiModal';
import { MuiReactImageCrop } from './MuiReactImageCrop';
import { Box } from '@mui/system';

const MAX_FILE_SIZE = 10000000;

const PREFIX = 'MuiImagePicker';

const classes = {
    paper: `${PREFIX}-paper`,
    row: `${PREFIX}-row`,
    column: `${PREFIX}-column`
};

interface BlobWithName extends Blob {
    name?: string;
}

interface MuiImageCropModalProps {
    onSelect: (imageUrl: string) => void;
    onSubmit: (blob: BlobWithName) => Promise<any>;
    height?: number;
    url?: string;
    // Due to upgrading material ui we had to do that, please remove legacy forms finally
    width?: number | any;
    aspect?: number;
    uploadFileName?: string;
    src: string;
}

enum MuiImagePickerStep {
    CLOSED = 'CLOSED',
    FILE_CHOOSING = 'FILE_CHOOSING',
    CROPING = 'CROPING'
}

interface Crop {
    aspect: number;
    width: any;
    height: number;
    x: number;
    y: number;
}

const getDefaultCrop = (props: MuiImageCropModalProps): Crop => {
    let aspect: number;
    if (props.aspect) {
        // eslint-disable-next-line prefer-destructuring
        aspect = props.aspect;
    } else {
        aspect = props.width && props.height ? props.width / props.height : null;
    }
    return {
        aspect,
        width: props.width,
        height: props.height,
        x: 0,
        y: 0
    };
};

export const MuiImageCropModal: React.FC<MuiImageCropModalProps> = props => {
    const { src: initialSrc, onSubmit, uploadFileName } = props;
    const imageRef = React.useRef<HTMLImageElement>(null);
    const [croppedSrc, setCroppedSrc] = React.useState<string>(null);
    const [disabled, setDisabled] = React.useState(false);
    const [blob, setBlob] = React.useState<BlobWithName>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [imagePickerStep, setImagePickerStep] = React.useState<MuiImagePickerStep>(
        MuiImagePickerStep.CLOSED
    );
    const [crop, setCrop] = React.useState<Crop>(getDefaultCrop(props));

    React.useEffect(() => {
        if (isString(initialSrc)) {
            setCroppedSrc(initialSrc);
            setImagePickerStep(MuiImagePickerStep.CROPING);
        }
    }, [initialSrc]);

    const getCroppedImg = React.useCallback(
        async (image: HTMLImageElement, crop: Crop, fileName: string): Promise<{ blob: Blob }> => {
            const canvas = document.createElement('canvas');
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            canvas.width = crop.width * scaleX;
            canvas.height = crop.height * scaleY;
            const ctx = canvas.getContext('2d');

            ctx.drawImage(
                image,
                crop.x * scaleX,
                crop.y * scaleY,
                crop.width * scaleX,
                crop.height * scaleY,
                0,
                0,
                crop.width * scaleX,
                crop.height * scaleY
            );

            return new Promise((resolve, reject) => {
                canvas.toBlob((blob: BlobWithName) => {
                    if (!blob) {
                        reject(new Error('Canvas is empty'));
                    }
                    if (blob.size >= MAX_FILE_SIZE) {
                        reject(new Error('Maximum file size exceeded. File should be under 10M.'));
                    }
                    blob.name = fileName;
                    resolve({ blob });
                });
            });
        },
        []
    );

    const makeClientCrop = React.useCallback(
        async (crop: Crop) => {
            if (imageRef.current && crop.width && crop.height) {
                setIsLoading(true);
                setDisabled(true);
                setError(null);
                try {
                    const { blob } = await getCroppedImg(
                        imageRef.current,
                        crop,
                        uploadFileName ?? `${uuid()}` // For compatibility
                    );
                    setBlob(blob);
                    setIsLoading(false);
                    setError(null);
                    setDisabled(false);
                } catch (error) {
                    setIsLoading(false);
                    setError(error.message);
                }
            }
        },
        [getCroppedImg, uploadFileName]
    );

    const handleImageLoaded = React.useCallback(
        (image: HTMLImageElement) => {
            imageRef.current = image;
            makeClientCrop({ ...crop, height: image.height, width: image.width });
        },
        [crop, makeClientCrop]
    );

    const handleCropComplete = React.useCallback(
        (crop: Crop) => {
            makeClientCrop(crop);
        },
        [makeClientCrop]
    );

    const handleCropChange = React.useCallback((crop: Crop) => {
        setCrop(crop);
    }, []);

    const handleClose = React.useCallback(() => {
        setImagePickerStep(MuiImagePickerStep.CLOSED);
        setCroppedSrc(null);
        setCrop(getDefaultCrop(props));
    }, [props]);

    const handleSubmit = React.useCallback(async () => {
        setIsLoading(true);
        setDisabled(true);
        try {
            await onSubmit(blob);
            setIsLoading(false);
            setError(null);
            handleClose();
        } catch (err) {
            setError(err.message);
        }
    }, [blob, handleClose, onSubmit]);

    return croppedSrc ? (
        <MuiModal
            open={imagePickerStep === MuiImagePickerStep.CROPING}
            style={{ maxWidth: 800 }}
            onClose={handleClose}
            className={classes.paper}
        >
            <Column>
                <MuiReactImageCrop
                    src={croppedSrc}
                    crop={crop}
                    onImageLoaded={handleImageLoaded}
                    onComplete={handleCropComplete}
                    onChange={handleCropChange}
                />
                <Box paddingTop={1}>
                    <Row flex={1} align="space-between" valign="center" className={classes.row}>
                        <Column className={classes.column} valign="center">
                            <Typography color="error">{error}</Typography>
                        </Column>
                        <Row align="flex-end" gutter>
                            <Button color="secondary" onClick={handleClose} variant="outlined">
                                Cancel
                            </Button>
                            <LoadingButton
                                color="primary"
                                variant="contained"
                                onClick={handleSubmit}
                                loading={isLoading}
                                disabled={disabled}
                            >
                                Submit
                            </LoadingButton>
                        </Row>
                    </Row>
                </Box>
            </Column>
        </MuiModal>
    ) : null;
};
